import { QrCodeModal } from '@/features/profile/components/qr-code-modal';
import { User } from '@/types';
import { imageUrl } from '@/utils/image-url';
import Ionicons from '@expo/vector-icons/Ionicons';
import React, { useState } from 'react';
import { Image, Pressable, Text, View } from 'react-native';


interface ProfileHeaderProps {
  user: User;
  friendshipBadge?: React.ReactNode;
}

export function ProfileHeader({ user, friendshipBadge }: ProfileHeaderProps) {
  const [imageError, setImageError] = useState(false);

  const userName = user?.name || 'Unknown User';
  const rawHandle = user?.username || user?.displayUsername || '';
  const normalizedHandle = rawHandle.replace(/^@+/, '');
  const userUsername = normalizedHandle ? `@${normalizedHandle}` : '@unknown';
  const userAvatar = imageUrl(user?.image || user?.avatar, 'thumbnail');
  const [qrVisible, setQrVisible] = useState(false);

  const showIconFallback = !userAvatar || imageError;
  const avatarSize = 96;

  return (
    <View className="items-center mb-8">
      <View
        className="overflow-hidden border border-border bg-transparent mb-4"
        style={{
          width: avatarSize,
          height: avatarSize,
          borderRadius: avatarSize / 2,
          borderWidth: 1.5,
        }}
      >
        {!showIconFallback ? (
          <Image
            source={{ uri: userAvatar }}
            style={{
              width: '100%',
              height: '100%',
              borderRadius: avatarSize / 2,
            }}
            resizeMode="cover"
            onError={() => setImageError(true)}
            accessibilityLabel={`${userName}'s profile photo`}
            key={userAvatar}
          />
        ) : (
          <View className="w-full h-full bg-surface items-center justify-center">
            <Ionicons
              name="person-circle"
              size={avatarSize * 0.8}
              className="text-muted-foreground"
            />
          </View>
        )}
      </View>

      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Text
          className="font-bold text-2xl text-foreground"
          accessibilityRole="header"
        >
          {userName}
        </Text>
        {friendshipBadge}
      </View>

      <View className="flex-row items-center gap-1.5">
        <Text className="text-base text-muted-foreground">
          {userUsername}
        </Text>
        {normalizedHandle && (
          <Pressable
            onPress={() => setQrVisible(true)}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            accessibilityRole="button"
            accessibilityLabel="Share profile QR code"
          >
            <Ionicons
              name="qr-code-outline"
              size={20}
              className="text-muted-foreground"
            />
          </Pressable>
        )}
      </View>

      {user?.createdAt && (
        <Text className="text-sm mt-1 text-muted-foreground">
          Joined{' '}
          {new Date(user.createdAt).toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric',
          })}
        </Text>
      )}

      {user?.bio && (
        <Text className="text-base leading-6 mt-2 px-4 text-center text-muted-foreground">
          {user.bio}
        </Text>
      )}
      {user?.username && (
        <QrCodeModal
          visible={qrVisible}
          onClose={() => setQrVisible(false)}
          username={user.username}
          displayName={user.name}
        />
      )}
    </View>
  );
}
